import {
  IAssignDoctorQuery,
  ICreateBooking,
  IGetPriceQuery,
  IPatient
} from '../interfaces'
import useClinicOptionRooms from '../logic/useClinicOptionRooms'
import useSearchPatient from '../logic/useSearchPatient'
import ProfileInfo from './profileInfo'
import Symptom from './symptom'
import { LoadingOutlined } from '@ant-design/icons'
import { Col, Divider, Form, Row, Spin, notification } from 'antd'
import { useAuth } from 'auth'
import { Button, Select } from 'components/atoms'
import {
  FooterModal,
  FromDate,
  SelectRoom,
  ServiceSelection
} from 'components/templates'
import { ISubServicesSelected } from 'components/templates/serviceSelection'
import {
  EXAMINATION_TYPE_OPTIONS,
  PRIORITIZED_OPTIONS
} from 'constants/options'
import { EUnit } from 'enums/EUnit'
import { useModal, useProfile } from 'hooks'
import { t } from 'i18next'
import { IClinicRoom } from 'interfaces'
import { EBookingType, EExamStatus } from 'pages/patientBooking/enums'
import { IUpdateExamStatusQuery } from 'pages/patientBooking/interfaces'
import { EPaymentMethod } from 'pages/patientPayment/enums'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { useReceiveServices } from 'services'
import { commonSelector } from 'store/commonSlice'
import styled from 'styled-components'
import { StyleContentModal } from 'styles/styledGlobal'
import { formatDate, getCurrentDateWithStep, getDateAddMinute } from 'utils'

const StyledFooter = styled(FooterModal)`
  padding: 12px 24px;
`

interface IProps {
  patient?: IPatient
  ignoreNavigate?: boolean
}

const AddBookingModal = (props: IProps) => {
  const { patient } = props
  const [form] = Form.useForm()
  const modal = useModal()
  const { optionRooms } = useClinicOptionRooms()
  const { userId } = useProfile()
  const { isSysAdmin } = useAuth()
  const navigate = useNavigate()
  const { loading, patients, setLoading } = useSearchPatient()
  const { loading: modalLoading } = useSelector(commonSelector).modal
  const { createBooking, getPriceBooking } = useReceiveServices()
  const patientPrivate = patient || patients[0]
  const [isReadySubmit, setIsReadySubmit] = useState(false)
  const subServiceSelected = useRef<ISubServicesSelected>({})
  const [subServices, setSubServices] = useState([])
  const [activeReceive, setActiveReceive] = useState(false)
  const [isOrderFuture, setIsOrderFuture] = useState(false)
  const { patchAssignDoctor, updateExamStatus } = useReceiveServices()
  const [majorId, setMajorId] = useState<any>()
  const roomSelected = useRef<IClinicRoom>()
  const priceRef = useRef<any>({
    init_price: 0,
    price: 0,
    user_paying_amount: 0
  })
  useEffect(() => {
    setSubServices([])
  }, [majorId])
  useEffect(() => {
    if (optionRooms?.length === 1) {
      form.setFieldsValue({
        room: optionRooms[0].value,
        re_examination: 0,
        priority: 0
      })
      roomSelected.current = optionRooms[0]?.data
    }
  }, [optionRooms])
  const handleOnSubmit = async (isReceive?: boolean) => {
    const { fromTime, medical_history, re_examination, priority } =
      await form.validateFields()
    if (!medical_history?.description.trim()) {
      notification.error({ message: t('please-input-medical-history') })
      return
    }
    if (!subServiceSelected.current?.subServiceIds?.length && !re_examination) {
      notification.error({ message: t('please-select-service') })
      return
    }
    if (!fromTime?.time) {
      notification.error({ message: t('please-input-time') })
      return
    }
    setLoading(true)
    const imgIds = medical_history?.attachment_ids?.map((item: any) => item?.id)
    const query: ICreateBooking = {
      from: new Date(fromTime?.date).getTime(),
      to: getDateAddMinute(new Date(fromTime?.date), 30)?.getTime(),
      patient_id: patientPrivate.user_id,
      sub_service_ids: subServiceSelected.current?.subServiceIds,
      currency: EUnit.Point,
      booking_type: EBookingType.Offline,
      payment_method: EPaymentMethod.DirectPayment,
      clinic_id: userId,
      priority: priority,
      medical_history: {
        description: medical_history?.description.trim(),
        attachment_ids: imgIds
      },
      re_examination: !!re_examination,
      ...priceRef.current
    }
    createBooking(query)
      .then((res) => {
        if (res?._successfully) {
          notification.success({
            message: t('booking-success-at', {
              patient: patientPrivate.full_name,
              at: `${formatDate(
                form.getFieldValue('fromTime')?.time,
                'HH:mm'
              )} - ${formatDate(query?.to, 'HH:mm DD/MM/YYYY')}`
            })
          })
          if (roomSelected.current && res?.data?.booking_id) {
            handleAssignDoctor(
              roomSelected.current,
              res?.data?.booking_id,
              isReceive
            )
          }
          // if (res?.data?.booking_id && (!ignoreNavigate || isReceive)) {
          //   navigate(`/booking/receive/${res?.data?.booking_id}`)
          // }
          modal.close(false)
        }
      })
      .finally(() => setLoading(false))
  }

  const handelOnChangeServices = (data: ISubServicesSelected) => {
    subServiceSelected.current = data
    if (!data.subServiceIds?.length) return
    if (patientPrivate?.user_id) {
      const query: IGetPriceQuery = {
        clinic_id: userId,
        patient_id: patientPrivate?.user_id,
        sub_service_ids: data.subServiceIds || [],
        payment_method: EPaymentMethod.DirectPayment
      }
      getPriceBooking(query).then((res) => {
        if (res?._successfully) {
          priceRef.current = {
            init_price: res?.data?.init_price,
            price: res?.data?.price,
            user_paying_amount: res?.data?.patient_paying_amount
          }
        }
      })
    }
  }

  useEffect(() => {
    if (patientPrivate) {
      setIsReadySubmit(true)
    }
  }, [patientPrivate])

  const antIcon = <LoadingOutlined style={{ fontSize: 50 }} spin />

  const timeBooking = getCurrentDateWithStep(30)
  timeBooking.setTime(timeBooking.getTime())
  const currentTime = new Date()
  if (currentTime.getHours() === 23 && currentTime.getMinutes() > 30) {
    currentTime.setTime(timeBooking.getTime())
  }
  const handleAssignDoctor = async (
    room: IClinicRoom,
    bookingId: number,
    isReceive?: boolean
  ) => {
    const query = {
      booking_id: bookingId,
      doctor_id: room?.doctor?.ID,
      doctor_major_id: room?.doctor?.MajorID
    } as IAssignDoctorQuery
    const res = await patchAssignDoctor(query)
    if (res?._successfully && isReceive) {
      const res = await updateExamStatus({
        booking_id: bookingId,
        status: EExamStatus.Waiting
      } as IUpdateExamStatusQuery)
      await updateExamStatus({
        booking_id: bookingId,
        status: EExamStatus.InProgress
      })
      if (res._successfully && isSysAdmin) {
        navigate(`/examination/detail/${bookingId}`)
      } else
        updateExamStatus({
          booking_id: bookingId,
          status: EExamStatus.InProgress
        })
    }
  }
  const handleOnChangeField = (data: Array<any>) => {
    const valueChange = data?.[0]
    if (valueChange?.name[0] === 'fromTime') {
      const isCreateNow =
        formatDate(new Date(valueChange.value?.date), 'DD/MM/yyyy HH:mm') !==
        formatDate(new Date(timeBooking), 'DD/MM/yyyy HH:mm')
      setIsOrderFuture(isCreateNow)
    }
  }

  return (
    <Spin spinning={loading} indicator={antIcon}>
      <StyleContentModal>
        <Form
          form={form}
          onFieldsChange={handleOnChangeField}
          initialValues={{
            fromTime: {
              date: timeBooking,
              time: timeBooking
            }
          }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="fromTime"
                rules={[
                  {
                    required: true,
                    message: t('please-enter-info') || ''
                  }
                ]}>
                <FromDate required minDate={new Date()} minTime={timeBooking} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="re_examination">
                <Select
                  title={t('examination-type')}
                  options={EXAMINATION_TYPE_OPTIONS}
                  placeholder="N/A"
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="priority">
                <Select
                  title={t('prioritized')}
                  options={PRIORITIZED_OPTIONS}
                  placeholder="N/A"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="room">
                <SelectRoom
                  title={t('appointment-clinic')}
                  options={optionRooms}
                  onChange={(room, op) => {
                    roomSelected.current = op?.data
                    setActiveReceive(!!op)
                    setMajorId(roomSelected.current?.doctor?.MajorID)
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="medical_history">
                <Symptom />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Divider plain>{t('patient-info').toUpperCase()}</Divider>
        <ProfileInfo patient={patientPrivate} />
        <Divider plain>{t('service-list').toUpperCase()}</Divider>
        <ServiceSelection
          title={true}
          value={subServices}
          majorId={majorId}
          onChange={(subServices: any, value: any) => {
            setSubServices(subServices)
            handelOnChangeServices(value)
          }}
        />
      </StyleContentModal>
      <StyledFooter
        cancelLabel={t('close')}
        disabled={!isReadySubmit}
        onSubmit={() => handleOnSubmit()}>
        <Button
          loading={loading || modalLoading}
          type="primary"
          onClick={() => handleOnSubmit(true)}
          disabled={
            loading ||
            modalLoading ||
            !isReadySubmit ||
            isOrderFuture ||
            (!activeReceive && optionRooms.length !== 1)
          }>
          {t('examine-immediately')}
        </Button>
      </StyledFooter>
    </Spin>
  )
}

export default AddBookingModal
